import React, { useContext, useEffect, useState } from "react";
import { Box, Button, Chip, CircularProgress, Paper, Stack, TextField, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { useApiClient, useAppSelector } from "app/hooks";
import Api from "api";
import { Metaform, MetaformMemberRole, MetaformVisibility } from "generated/client";
import { ErrorContext } from "components/contexts/error-handler";
import strings from "localization/strings";
import { selectLocale } from "features/locale-slice";
import { selectKeycloak } from "features/auth-slice";
import AuthUtils from "utils/auth-utils";

interface SurveyRow {
  id: string;
  slug?: string;
  title: string;
  latestReply?: Date | null;
  unprocessedReplies?: number | null;
  visibility?: MetaformVisibility;
  hasStatisticsError?: boolean;
}

type SurveyFilter = "all" | "public" | "private";

const wideTableColumns = "minmax(190px, 2fr) minmax(110px, 1fr) 100px 70px 260px";
const compactTableColumns = "minmax(170px, 2fr) minmax(105px, 1fr) 96px 60px";
const responsiveTableColumns = {
  xs: wideTableColumns,
  sm: compactTableColumns,
  lg: wideTableColumns
};

/**
 * Displays API-backed surveys in the Granlund management layout.
 */
const FormsScreen: React.FC = () => {
  const errorContext = useContext(ErrorContext);
  const apiClient = useApiClient(Api.getApiClient);
  const { metaformsApi, metaformStatisticsApi } = apiClient;
  const navigate = useNavigate();
  const { locale } = useAppSelector(selectLocale);
  const keycloak = useAppSelector(selectKeycloak);
  const [ rows, setRows ] = useState<SurveyRow[]>([]);
  const [ loading, setLoading ] = useState(false);
  const [ search, setSearch ] = useState("");
  const [ filter, setFilter ] = useState<SurveyFilter>("all");
  const isFinnish = locale === "fi";
  const canCreateSurvey = AuthUtils.isGlobalFormAdmin(keycloak);

  const copy = isFinnish ? {
    heading: "Kyselyt",
    description: "Hallitse kyselyitä, vastauksia ja julkaisuja.",
    search: "Hae kyselyä tai kohdetunnusta",
    all: "Kaikki",
    public: "Julkaistu",
    private: "Yksityinen",
    newSurvey: "Uusi kysely",
    survey: "Kysely",
    building: "Kohdetunnus",
    status: "Tila",
    replies: "Vastauksia",
    latestReply: "Viimeisin vastaus",
    edit: "Muokkaa",
    report: "Raportti",
    respond: "Vastauslinkki",
    noSurveys: "Hakuehdoilla ei löytynyt kyselyitä.",
    statisticsUnavailable: "Vastaustietoja ei voitu ladata"
  } : {
    heading: "Surveys",
    description: "Manage surveys, responses, and publications.",
    search: "Search survey or building ID",
    all: "All",
    public: "Published",
    private: "Private",
    newSurvey: "New survey",
    survey: "Survey",
    building: "Building ID",
    status: "Status",
    replies: "Replies",
    latestReply: "Latest reply",
    edit: "Edit",
    report: "Report",
    respond: "Response link",
    noSurveys: "No surveys match the current filters.",
    statisticsUnavailable: "Response statistics could not be loaded"
  };

  /**
   * Converts a Metaform to a visible row before statistics are available.
   */
  const buildPlaceholderRow = (form: Metaform): SurveyRow | undefined => {
    if (!form.id || !form.slug) return undefined;

    return {
      id: form.id,
      slug: form.slug,
      title: form.title || strings.formScreen.noTitle,
      latestReply: null,
      unprocessedReplies: null,
      visibility: form.visibility
    };
  };

  /**
   * Loads the response statistics required for a survey row.
   */
  const buildRow = async (form: Metaform): Promise<SurveyRow | undefined> => {
    const placeholder = buildPlaceholderRow(form);
    if (!placeholder) return undefined;

    try {
      const statistics = await metaformStatisticsApi.getStatistics({ metaformId: placeholder.id });
      return {
        ...placeholder, latestReply: statistics.lastReplyDate, unprocessedReplies: statistics.unprocessedReplies
      };
    } catch {
      return { ...placeholder, hasStatisticsError: true };
    }
  };

  useEffect(() => {
    /**
     * Loads initial survey data followed by response statistics.
     */
    const loadSurveys = async () => {
      setLoading(true);
      try {
        const forms = await metaformsApi.listMetaforms({ memberRole: MetaformMemberRole.Manager });
        setRows(forms.map(buildPlaceholderRow).filter((row): row is SurveyRow => Boolean(row)));
        setRows((await Promise.all(forms.map(buildRow))).filter((row): row is SurveyRow => Boolean(row)));
      } catch (error) {
        errorContext.setError(strings.errorHandling.adminFormsScreen.listForms, error);
      } finally {
        setLoading(false);
      }
    };

    loadSurveys();
  }, []);

  const filteredRows = rows.filter(row => {
    const matchesSearch = `${row.title} ${row.slug || ""}`.toLocaleLowerCase().includes(search.toLocaleLowerCase());
    const status = row.visibility === MetaformVisibility.Public ? "public" : "private";
    return matchesSearch && (filter === "all" || filter === status);
  });

  return (
    <Box sx={{
      width: "100%"
    }}
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={ 1 }
        useFlexGap
        flexWrap="wrap"
        alignItems={{ xs: "stretch", md: "center" }}
        mb={ 2.25 }
      >
        <TextField
          value={ search }
          onChange={ event => setSearch(event.target.value) }
          placeholder={ copy.search }
          sx={{
            flex: { md: "1 1 300px" },
            minWidth: { md: 300 },
            maxWidth: { md: 340 },
            "& .MuiInputBase-root": {
              backgroundColor: "#fff",
              color: "#1a1a1a",
              fontSize: 14,
              lineHeight: "20px"
            },
            "& .MuiInputBase-input::placeholder": {
              color: "#6e7472",
              fontSize: 14,
              opacity: 1
            }
          }}
        />
        <Stack direction="row" spacing={ 0.75 } flexWrap="wrap" useFlexGap>
          { ([ ["all", copy.all], ["public", copy.public], ["private", copy.private] ] as [SurveyFilter, string][]).map(([ value, label ]) => (
            <Button
              key={ value }
              size="small"
              variant={ filter === value ? "contained" : "outlined" }
              onClick={ () => setFilter(value) }
              sx={{
                minHeight: 33,
                px: 1.75,
                py: 0.5,
                borderColor: filter === value ? "#278062" : "#d9dedb",
                backgroundColor: filter === value ? "#278062" : "#fff",
                color: filter === value ? "#fff" : "#4d4d4d",
                fontSize: 13,
                fontWeight: 500,
                lineHeight: "20px",
                "&:hover": {
                  borderColor: filter === value ? "#1c5f49" : "#bfc7c3",
                  backgroundColor: filter === value ? "#1c5f49" : "#f4f6f5"
                }
              }}
            >
              { label }
            </Button>
          )) }
        </Stack>
        { canCreateSurvey &&
          <Button
            variant="contained"
            onClick={ () => navigate("/admin/editor") }
            sx={{
              ml: { md: "auto !important" },
              alignSelf: { xs: "stretch", md: "auto" },
              px: 2.25,
              py: 0.875,
              borderRadius: 1,
              backgroundColor: "#ED8113",
              color: "#fff",
              fontSize: 14,
              fontWeight: 500,
              "&:hover": {
                backgroundColor: "#c86b0e"
              }
            }}
          >
            { copy.newSurvey }
          </Button>
        }
      </Stack>
      <Paper
        variant="outlined"
        sx={{
          borderColor: "#e2e5e3", borderRadius: 1, overflow: "hidden"
        }}
      >
        <Box sx={{ overflowX: "auto" }}>
          <Box sx={{ minWidth: { xs: 900, sm: 0 } }}>
            <Box sx={{
              display: "grid", gridTemplateColumns: responsiveTableColumns, gap: 2, px: 2.25, py: 1.25, bgcolor: "#fafbfa", borderBottom: "1px solid #e2e5e3", color: "#6e7472", fontSize: 11, fontWeight: 700, letterSpacing: "0.7px", textTransform: "uppercase"
            }}
            >
              <Box>{ copy.survey }</Box>
              <Box>{ copy.building }</Box>
              <Box>{ copy.status }</Box>
              <Box textAlign="right">{ copy.replies }</Box>
              <Box
                display={{
                  xs: "block", sm: "none", lg: "block"
                }}
              />
            </Box>
            { loading && <Box display="flex" justifyContent="center" p={ 4 }><CircularProgress size={ 28 }/></Box> }
            { !loading && filteredRows.map(row => {
              const isPublic = row.visibility === MetaformVisibility.Public;
              let replySummary = "-";
              if (row.hasStatisticsError) {
                replySummary = copy.statisticsUnavailable;
              } else if (row.latestReply) {
                replySummary = `${copy.latestReply}: ${moment(row.latestReply).format("L LT")}`;
              }

              return (
                <Box
                  key={ row.id }
                  sx={{
                    display: "grid", gridTemplateColumns: responsiveTableColumns, gap: 2, alignItems: "center", px: 2.25, py: 1.25, borderTop: "1px solid #f0f2f1"
                  }}
                >
                  <Box minWidth={ 0 }>
                    <Typography
                      noWrap
                      sx={{
                        fontSize: 14, fontWeight: 500, lineHeight: "20px"
                      }}
                    >
                      { row.title }
                    </Typography>
                    <Typography
                      sx={{
                        color: "#8a8f8c", fontSize: 12, lineHeight: "18px"
                      }}
                    >
                      { replySummary }
                    </Typography>
                  </Box>
                  <Typography
                    sx={{
                      color: "#8a8f8c",
                      fontFamily: "ui-monospace, monospace",
                      fontSize: 11.5,
                      lineHeight: "18px"
                    }}
                  >
                    { row.slug || "-" }
                  </Typography>
                  <Box>
                    <Chip
                      label={ isPublic ? copy.public : copy.private }
                      size="small"
                      sx={{
                        bgcolor: isPublic ? "#e4efe9" : "#fdf0e0", color: isPublic ? "#1c5f49" : "#a55b06", borderRadius: 0.75, fontWeight: 600
                      }}
                    />
                  </Box>
                  <Typography textAlign="right" sx={{ fontSize: 14, fontWeight: 500 }}>{ row.unprocessedReplies ?? "-" }</Typography>
                  <Stack
                    direction="row"
                    spacing={ 0.5 }
                    justifyContent={{
                      xs: "flex-end", sm: "flex-start", lg: "flex-end"
                    }}
                    gridColumn={{
                      xs: "auto", sm: "1 / -1", lg: "auto"
                    }}
                  >
                    <Button
                      size="small"
                      onClick={ () => navigate("/admin/editor") }
                      sx={{
                        px: 1.125, py: 0.375, borderColor: "rgba(39, 128, 98, 0.5)", color: "#278062", fontSize: 13, fontWeight: 500, lineHeight: "20px"
                      }}
                      variant="outlined"
                    >
                      { copy.edit }
                    </Button>
                    <Button
                      size="small"
                      onClick={ () => navigate(`/admin/forms/${row.slug}/answers`) }
                      sx={{
                        px: 1.125, py: 0.375, borderColor: "#d9dedb", color: "#4d4d4d", fontSize: 13, fontWeight: 500, lineHeight: "20px"
                      }}
                      variant="outlined"
                    >
                      { copy.report }
                    </Button>
                    <Button
                      size="small"
                      onClick={ () => window.open(`/${row.slug}`, "_blank", "noopener,noreferrer") }
                      sx={{
                        px: 1.125, py: 0.375, borderColor: "#d9dedb", color: "#4d4d4d", fontSize: 13, fontWeight: 500, lineHeight: "20px"
                      }}
                      variant="outlined"
                    >
                      { copy.respond }
                    </Button>
                  </Stack>
                </Box>
              );
            }) }
            { !loading && !filteredRows.length && <Typography p={ 3 } color="text.secondary">{ copy.noSurveys }</Typography> }
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default FormsScreen;